import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { journalAPI, accountsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { fmtMoney } from '../../utils/currency';

const fmt = fmtMoney;

export default function JournalEntryForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [entry, setEntry] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    posting_date: new Date().toISOString().split('T')[0],
    description: '',
    reference: '',
    lines: [
      { account_id: '', description: '', debit: '', credit: '' },
      { account_id: '', description: '', debit: '', credit: '' }
    ]
  });
  const [viewData, setViewData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    accountsAPI.list({ active: 'true' }).then(setAccounts).catch(console.error);
    if (id) journalAPI.get(id).then(setViewData).catch(console.error);
  }, [id]);

  const totalDebit = entry.lines.reduce((s, l) => s + parseFloat(l.debit || 0), 0);
  const totalCredit = entry.lines.reduce((s, l) => s + parseFloat(l.credit || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const addLine = () => setEntry({ ...entry, lines: [...entry.lines, { account_id: '', description: '', debit: '', credit: '' }] });

  const removeLine = (i) => {
    if (entry.lines.length <= 2) return;
    setEntry({ ...entry, lines: entry.lines.filter((_, idx) => idx !== i) });
  };

  const updateLine = (i, field, value) => {
    const lines = [...entry.lines];
    lines[i] = { ...lines[i], [field]: value };
    if (field === 'debit' && value) lines[i].credit = '';
    if (field === 'credit' && value) lines[i].debit = '';
    setEntry({ ...entry, lines });
  };

  const handleSubmit = async (autoPost = false) => {
    if (!isBalanced) return toast.error('Debits must equal credits');
    if (totalDebit === 0) return toast.error('Entry must have amounts');
    setLoading(true);
    try {
      const data = {
        ...entry,
        company_id: 0,
        autoPost,
        lines: entry.lines.filter(l => l.account_id && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0))
      };
      await journalAPI.create(data);
      toast.success(autoPost ? 'Journal entry posted!' : 'Journal entry saved as draft');
      navigate('/journal');
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const handlePost = async () => {
    setLoading(true);
    try {
      await journalAPI.post(id);
      toast.success('Entry posted!');
      setViewData(null);
      journalAPI.get(id).then(setViewData);
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  const handleVoid = async () => {
    const reason = prompt('Reason for voiding:');
    if (!reason) return;
    setLoading(true);
    try {
      await journalAPI.void(id, { reason });
      toast.success('Entry voided');
      journalAPI.get(id).then(setViewData);
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  // View mode
  if (viewData) {
    return (
      <div>
        <div className="toolbar">
          <h2>Journal Entry: {viewData.entry_number}</h2>
          <div className="toolbar-right">
            <span className={`badge badge-${viewData.status?.toLowerCase()}`} style={{ fontSize: 11, padding: '3px 8px' }}>{viewData.status}</span>
            {viewData.status === 'Draft' && hasPermission('journal:post') && (
              <button className="btn btn-primary" onClick={handlePost} disabled={loading}>Post Entry</button>
            )}
            {viewData.status === 'Posted' && hasPermission('journal:void') && (
              <button className="btn btn-danger" onClick={handleVoid} disabled={loading}>Void Entry</button>
            )}
            <button className="btn btn-outline" onClick={() => navigate('/journal')}>Back to List</button>
          </div>
        </div>

        <div className="df-editor" style={{ marginBottom: 12 }}>
          <div className="df-section-header"><span>Entry Details</span></div>
          <div className="df-body">
            <div className="df-grid cols-3">
              <div className="df-field">
                <label>Entry Number</label>
                <input value={viewData.entry_number} disabled />
              </div>
              <div className="df-field">
                <label>Entry Date</label>
                <input value={viewData.entry_date} disabled />
              </div>
              <div className="df-field">
                <label>Source</label>
                <input value={viewData.source_module || 'Manual'} disabled />
              </div>
              <div className="df-field">
                <label>Description</label>
                <input value={viewData.description} disabled />
              </div>
              <div className="df-field">
                <label>Reference</label>
                <input value={viewData.reference || '-'} disabled />
              </div>
              <div className="df-field">
                <label>Posted By</label>
                <input value={viewData.posted_by_name || '-'} disabled />
              </div>
            </div>
          </div>
        </div>

        <div className="df-editor">
          <div className="df-section-header"><span>Entry Lines</span></div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', width: 60 }}>#</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>Account</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>Description</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', width: 130 }}>Debit</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', width: 130 }}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {(viewData.lines || []).map((l, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '6px 10px', fontSize: 12, color: '#94a3b8' }}>{l.line_number}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12 }}><span style={{ fontFamily: 'monospace', fontSize: 11 }}>{l.account_code}</span> {l.account_name}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12 }}>{l.description}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.debit > 0 ? fmt(l.debit) : ''}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.credit > 0 ? fmt(l.credit) : ''}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 700, borderTop: '2px solid #e5e7eb', background: '#f8fafc' }}>
                  <td colSpan={3} style={{ padding: '8px 10px', fontSize: 12, textAlign: 'right', textTransform: 'uppercase', letterSpacing: '.3px', color: '#475569' }}>Total</td>
                  <td style={{ padding: '8px 10px', fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(viewData.total_debit)}</td>
                  <td style={{ padding: '8px 10px', fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(viewData.total_credit)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Create mode
  return (
    <div>
      <div className="toolbar">
        <h2>New Journal Entry</h2>
        <div className="toolbar-right">
          <button className="btn btn-outline" onClick={() => navigate('/journal')}>Cancel</button>
          <button className="btn btn-primary" onClick={() => handleSubmit(true)} disabled={loading || !isBalanced}>
            Save &amp; Post
          </button>
          <button className="btn btn-success" onClick={() => handleSubmit(false)} disabled={loading}>
            Save as Draft
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="df-editor" style={{ marginBottom: 12 }}>
        <div className="df-section-header"><span>Entry Details</span></div>
        <div className="df-body">
          <div className="df-grid cols-3">
            <div className="df-field">
              <label>Entry Date <span className="req">*</span></label>
              <input type="date" value={entry.entry_date} onChange={e => setEntry({ ...entry, entry_date: e.target.value, posting_date: e.target.value })} />
            </div>
            <div className="df-field">
              <label>Reference</label>
              <input value={entry.reference} onChange={e => setEntry({ ...entry, reference: e.target.value })} placeholder="Optional" />
            </div>
            <div className="df-field">
              <label>Description <span className="req">*</span></label>
              <input value={entry.description} onChange={e => setEntry({ ...entry, description: e.target.value })} placeholder="Description" />
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className="df-editor">
        <div className="df-section-header">
          <span>Entry Lines</span>
          <span style={{ display: 'flex', gap: 12, alignItems: 'center', textTransform: 'none', letterSpacing: 0, fontSize: 11, fontWeight: 400 }}>
            <span>Debit: <strong>{fmt(totalDebit)}</strong></span>
            <span>Credit: <strong>{fmt(totalCredit)}</strong></span>
            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: isBalanced ? '#dcfce7' : '#fef2f2', color: isBalanced ? '#166534' : '#dc2626' }}>
              {isBalanced ? 'BALANCED' : 'UNBALANCED'}
            </span>
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', width: 40 }}>#</th>
                <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', width: 250 }}>Account</th>
                <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>Description</th>
                <th style={{ textAlign: 'right', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', width: 130 }}>Debit</th>
                <th style={{ textAlign: 'right', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', width: 130 }}>Credit</th>
                <th style={{ padding: '6px 6px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb', width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {entry.lines.map((line, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                  <td style={{ padding: '4px 10px', fontSize: 12, color: '#94a3b8' }}>{i + 1}</td>
                  <td style={{ padding: '4px 6px' }}>
                    <select style={{ width: '100%', height: 24, padding: '0 6px', border: '1px solid #d1d5db', borderRadius: 2, fontSize: 11, background: '#fff' }} value={line.account_id} onChange={e => updateLine(i, 'account_id', e.target.value)}>
                      <option value="">Select account...</option>
                      {accounts.map(a => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input style={{ width: '100%', height: 24, padding: '0 6px', border: '1px solid #d1d5db', borderRadius: 2, fontSize: 11 }} value={line.description} onChange={e => updateLine(i, 'description', e.target.value)} placeholder="Description" />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input style={{ width: '100%', height: 24, padding: '0 6px', border: '1px solid #d1d5db', borderRadius: 2, fontSize: 11, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} type="number" step="0.01" min="0" value={line.debit} onChange={e => updateLine(i, 'debit', e.target.value)} placeholder="0.00" />
                  </td>
                  <td style={{ padding: '4px 6px' }}>
                    <input style={{ width: '100%', height: 24, padding: '0 6px', border: '1px solid #d1d5db', borderRadius: 2, fontSize: 11, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} type="number" step="0.01" min="0" value={line.credit} onChange={e => updateLine(i, 'credit', e.target.value)} placeholder="0.00" />
                  </td>
                  <td style={{ padding: '4px', textAlign: 'center' }}>
                    <button onClick={() => removeLine(i)} disabled={entry.lines.length <= 2} style={{ background: 'none', border: 'none', color: entry.lines.length <= 2 ? '#d1d5db' : '#94a3b8', cursor: entry.lines.length <= 2 ? 'default' : 'pointer', fontSize: 13, padding: '2px 4px' }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '6px 10px', borderTop: '1px solid #e5e7eb' }}>
          <button type="button" onClick={addLine} style={{ background: 'none', border: '1px dashed #d1d5db', borderRadius: 2, padding: '3px 10px', fontSize: 11, color: '#64748b', cursor: 'pointer' }}>
            + Add Line
          </button>
        </div>
      </div>
    </div>
  );
}
